import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Stack, Paper, Alert, CircularProgress,
  Chip, Divider, IconButton, Tooltip, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, InputAdornment, Collapse,
  Link, Tabs, Tab,
} from '@mui/material';
import {
  ContentPaste as PasteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MedicalServices as MedicalIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface HealthEntity {
  text: string;
  category: string;
  offset: number;
  length: number;
  confidenceScore: number;
  assertion?: { certainty?: string; conditionality?: string; association?: string };
  links?: Array<{ dataSource: string; id: string }>;
  normalizedText?: string;
}

interface AnalysisResult {
  entities: HealthEntity[];
  relations: Array<{ relationType: string; entities: Array<{ ref: string; role: string }> }>;
}

interface ICD10Result {
  code: string;
  name: string;
}

interface AzureConfig {
  endpoint: string;
  key: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────────

const NLM_API = 'https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search';
const AZURE_API_VERSION = '2024-11-15-preview';
const AZURE_CONFIG_KEY = 'famelia_azure_health_config';

const EXAMPLE_TEXT = `Assessment: Patient presents with type 2 diabetes mellitus with diabetic chronic kidney disease, stage 3. 
Also noted: essential hypertension, well-controlled on current medications. 
BMI 32.4, class I obesity. 
History of gastroesophageal reflux disease, currently managed with omeprazole.`;

// ─── Helpers ────────────────────────────────────────────────────────────────────

function loadAzureConfig(): AzureConfig {
  try {
    const raw = localStorage.getItem(AZURE_CONFIG_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return {
    endpoint: process.env.REACT_APP_AZURE_LANGUAGE_ENDPOINT || '',
    key: process.env.REACT_APP_AZURE_LANGUAGE_KEY || '',
  };
}

function saveAzureConfig(cfg: AzureConfig) {
  localStorage.setItem(AZURE_CONFIG_KEY, JSON.stringify(cfg));
}

function getCategoryColor(cat: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' {
  switch (cat) {
    case 'Diagnosis': return 'error';
    case 'SymptomOrSign': return 'warning';
    case 'MedicationName': return 'info';
    case 'DosageAmount':
    case 'MedicationForm':
    case 'MedicationRoute': return 'secondary';
    case 'BodyStructure': return 'success';
    case 'ExaminationName':
    case 'TreatmentName': return 'primary';
    default: return 'default';
  }
}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function ICD10CoderTab() {
  const [mode, setMode] = useState(0); // 0 = text analysis, 1 = code search, 2 = settings
  const [clinicalText, setClinicalText] = useState('');
  const [analysisResults, setAnalysisResults] = useState<AnalysisResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ICD10Result[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [azureConfig, setAzureConfig] = useState<AzureConfig>(loadAzureConfig);
  const [configSaved, setConfigSaved] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedEntity, setExpandedEntity] = useState<number | null>(null);

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Azure Text Analytics for Health ─────────────────────────────────────────

  const analyzeText = useCallback(async () => {
    if (!clinicalText.trim()) return;
    if (!azureConfig.endpoint || !azureConfig.key) {
      setAnalysisError('Azure Language endpoint and key are required. Configure them in the Settings tab.');
      return;
    }

    setAnalyzing(true);
    setAnalysisError(null);
    setAnalysisResults(null);

    try {
      const baseUrl = azureConfig.endpoint.replace(/\/$/, '');
      const submitUrl = `${baseUrl}/language/analyze-text/jobs?api-version=${AZURE_API_VERSION}`;

      const submitRes = await fetch(submitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': azureConfig.key,
        },
        body: JSON.stringify({
          analysisInput: {
            documents: [{ id: '1', language: 'en', text: clinicalText }],
          },
          tasks: [{
            kind: 'Healthcare',
            taskName: 'icd10-extraction',
          }],
        }),
      });

      if (!submitRes.ok) {
        const errBody = await submitRes.text();
        throw new Error(`Azure API error (${submitRes.status}): ${errBody}`);
      }

      const operationLocation = submitRes.headers.get('operation-location');
      if (!operationLocation) throw new Error('No operation-location header in response');

      let result: any = null;
      for (let i = 0; i < 30; i++) {
        await sleep(1000);
        const pollRes = await fetch(operationLocation, {
          headers: { 'Ocp-Apim-Subscription-Key': azureConfig.key },
        });
        if (!pollRes.ok) throw new Error(`Poll failed (${pollRes.status})`);
        const pollData = await pollRes.json();

        if (pollData.status === 'succeeded') {
          result = pollData;
          break;
        } else if (pollData.status === 'failed' || pollData.status === 'cancelled') {
          throw new Error(`Analysis ${pollData.status}: ${JSON.stringify(pollData.errors ?? [])}`);
        }
      }

      if (!result) throw new Error('Analysis timed out after 30 seconds');

      const healthTask = result.tasks?.items?.[0];
      const doc = healthTask?.results?.documents?.[0];
      if (!doc) throw new Error('No results returned from analysis');

      setAnalysisResults({
        entities: doc.entities ?? [],
        relations: doc.relations ?? [],
      });
    } catch (err: any) {
      setAnalysisError(err.message ?? 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  }, [clinicalText, azureConfig]);

  // ── NLM ICD-10-CM Search ────────────────────────────────────────────────────

  const searchICD10 = useCallback(async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      setSearchTotal(0);
      return;
    }

    setSearching(true);
    setSearchError(null);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 10000);

      const url = `${NLM_API}?sf=code,name&terms=${encodeURIComponent(term.trim())}&maxList=50&df=code,name`;
      const res = await fetch(url, { signal: controller.signal, mode: 'cors' });
      clearTimeout(timeout);

      if (!res.ok) throw new Error(`NLM API returned status ${res.status}`);
      const data = await res.json();

      if (!Array.isArray(data) || data.length < 4) {
        throw new Error('Unexpected response format from ICD-10 API');
      }

      const total: number = typeof data[0] === 'number' ? data[0] : 0;
      const details: string[][] = Array.isArray(data[3]) ? data[3] : [];
      const results: ICD10Result[] = details
        .filter(row => Array.isArray(row) && row.length >= 2)
        .map(([code, name]) => ({ code, name }));

      setSearchResults(results);
      setSearchTotal(total);
    } catch (err: any) {
      const msg = err.name === 'AbortError'
        ? 'Search timed out. Please try again.'
        : err.message ?? 'Search failed';
      console.error('[ICD-10 Search]', err);
      setSearchError(msg);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (value.trim()) {
      searchDebounce.current = setTimeout(() => searchICD10(value), 400);
    } else {
      setSearchResults([]);
      setSearchTotal(0);
    }
  };

  const handleSearchSubmit = () => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchICD10(searchTerm);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  useEffect(() => {
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, []);

  // ── Copy to clipboard ──────────────────────────────────────────────────────

  const copyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch { /* fallback */ }
  };

  // ── Settings save ──────────────────────────────────────────────────────────

  const handleSaveConfig = () => {
    saveAzureConfig(azureConfig);
    setConfigSaved(true);
    setTimeout(() => setConfigSaved(false), 2500);
  };

  // ── Diagnosis entities with ICD-10 codes ───────────────────────────────────

  const diagnosisEntities = analysisResults?.entities.filter(e =>
    e.category === 'Diagnosis' || e.category === 'SymptomOrSign'
  ) ?? [];

  const entitiesWithICD = diagnosisEntities.filter(e =>
    e.links?.some(l => l.dataSource === 'ICD10CM' || l.dataSource === 'ICD-10-CM' || l.dataSource === 'ICD9CM')
  );

  const isAzureConfigured = !!(azureConfig.endpoint && azureConfig.key);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MedicalIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>ICD-10 Diagnosis Coder</Typography>
        </Stack>
        <Chip
          label="Powered by Microsoft Azure AI + NLM"
          size="small"
          variant="outlined"
          color="primary"
        />
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Paste de-identified chart notes to extract ICD-10 codes using{' '}
        <Link href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/text-analytics-for-health/overview" target="_blank" rel="noopener">
          Azure Text Analytics for Health
        </Link>
        , or search the ICD-10-CM code database directly. No PHI should be entered.
      </Alert>

      <Tabs
        value={mode}
        onChange={(_, v) => setMode(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab
          icon={<PasteIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Analyze Chart Notes"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        />
        <Tab
          icon={<SearchIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Search ICD-10 Codes"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        />
        <Tab
          icon={<SettingsIcon sx={{ fontSize: 18 }} />}
          iconPosition="start"
          label="Azure Settings"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        />
      </Tabs>

      {/* ══════════ Tab 0: Clinical Text Analysis ══════════ */}
      {mode === 0 && (
        <Box>
          {!isAzureConfigured && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Azure Language endpoint and key not configured.{' '}
              <Link component="button" onClick={() => setMode(2)} sx={{ fontWeight: 600 }}>
                Configure in Settings
              </Link>{' '}
              to use the clinical text analyzer.
            </Alert>
          )}

          <TextField
            multiline
            rows={8}
            fullWidth
            placeholder="Paste de-identified chart notes here (diagnoses, assessments, clinical impressions)..."
            value={clinicalText}
            onChange={e => setClinicalText(e.target.value)}
            sx={{
              mb: 2,
              '& .MuiInputBase-root': {
                fontFamily: '"Segoe UI", -apple-system, sans-serif',
                fontSize: '0.95rem',
                lineHeight: 1.6,
              },
            }}
          />

          <Stack direction="row" spacing={1} mb={3}>
            <Button
              variant="contained"
              onClick={analyzeText}
              disabled={analyzing || !clinicalText.trim() || !isAzureConfigured}
              startIcon={analyzing ? <CircularProgress size={18} color="inherit" /> : <MedicalIcon />}
              sx={{ textTransform: 'none', borderRadius: 5, fontWeight: 600 }}
            >
              {analyzing ? 'Analyzing...' : 'Extract ICD-10 Codes'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setClinicalText(EXAMPLE_TEXT)}
              disabled={analyzing}
              sx={{ textTransform: 'none', borderRadius: 5 }}
            >
              Load Example
            </Button>
            {clinicalText && (
              <Button
                variant="text"
                onClick={() => { setClinicalText(''); setAnalysisResults(null); setAnalysisError(null); }}
                startIcon={<ClearIcon />}
                sx={{ textTransform: 'none', borderRadius: 5 }}
              >
                Clear
              </Button>
            )}
          </Stack>

          {analysisError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAnalysisError(null)}>
              {analysisError}
            </Alert>
          )}

          {analysisResults && (
            <Box>
              <Divider sx={{ mb: 2 }} />

              {/* Summary cards */}
              <Stack direction="row" spacing={2} mb={3}>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, flex: 1, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {entitiesWithICD.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">ICD-10 Codes Found</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, flex: 1, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700} color="error.main">
                    {diagnosisEntities.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Diagnoses Detected</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1.5, flex: 1, textAlign: 'center' }}>
                  <Typography variant="h4" fontWeight={700}>
                    {analysisResults.entities.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Medical Entities</Typography>
                </Paper>
              </Stack>

              {/* ICD-10 Results Table */}
              {entitiesWithICD.length > 0 && (
                <>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Extracted ICD-10 Codes
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell><strong>ICD-10 Code</strong></TableCell>
                          <TableCell><strong>Diagnosis Text</strong></TableCell>
                          <TableCell><strong>Normalized</strong></TableCell>
                          <TableCell align="center"><strong>Confidence</strong></TableCell>
                          <TableCell><strong>Certainty</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {entitiesWithICD.map((entity, idx) => {
                          const icdLink = entity.links?.find(l =>
                            l.dataSource === 'ICD10CM' || l.dataSource === 'ICD-10-CM'
                          );
                          const icdCode = icdLink?.id ?? '—';
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>
                                <Chip
                                  label={icdCode}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontFamily: 'monospace', fontWeight: 700 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={500}>{entity.text}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {entity.normalizedText || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`${(entity.confidenceScore * 100).toFixed(0)}%`}
                                  size="small"
                                  color={entity.confidenceScore >= 0.8 ? 'success' : entity.confidenceScore >= 0.5 ? 'warning' : 'error'}
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontSize="0.8rem">
                                  {entity.assertion?.certainty ?? 'positive'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={copiedCode === icdCode ? 'Copied!' : 'Copy code'}>
                                  <IconButton size="small" onClick={() => copyCode(icdCode)}>
                                    {copiedCode === icdCode ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Quick-copy all codes */}
                  <Stack direction="row" spacing={1} mb={3} alignItems="center">
                    <Typography variant="body2" color="text.secondary">Quick copy all:</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const allCodes = entitiesWithICD.map(e => {
                          const l = e.links?.find(l => l.dataSource === 'ICD10CM' || l.dataSource === 'ICD-10-CM');
                          return l?.id;
                        }).filter(Boolean).join(', ');
                        copyCode(allCodes);
                      }}
                      startIcon={<CopyIcon />}
                      sx={{ textTransform: 'none', borderRadius: 5 }}
                    >
                      All Codes
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const lines = entitiesWithICD.map(e => {
                          const l = e.links?.find(l => l.dataSource === 'ICD10CM' || l.dataSource === 'ICD-10-CM');
                          return `${l?.id ?? '—'}\t${e.normalizedText || e.text}`;
                        }).join('\n');
                        copyCode(lines);
                      }}
                      startIcon={<CopyIcon />}
                      sx={{ textTransform: 'none', borderRadius: 5 }}
                    >
                      Codes + Descriptions
                    </Button>
                  </Stack>
                </>
              )}

              {/* All entities (expandable) */}
              <Typography variant="h6" fontWeight={600} mb={1}>
                All Detected Medical Entities
              </Typography>
              <Stack spacing={0.5}>
                {analysisResults.entities.map((entity, idx) => (
                  <Paper key={idx} variant="outlined" sx={{ borderRadius: 1.5 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ px: 2, py: 1, cursor: 'pointer' }}
                      onClick={() => setExpandedEntity(expandedEntity === idx ? null : idx)}
                    >
                      <Chip label={entity.category} size="small" color={getCategoryColor(entity.category)} variant="outlined" />
                      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                        {entity.text}
                      </Typography>
                      <Chip
                        label={`${(entity.confidenceScore * 100).toFixed(0)}%`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      {entity.links && entity.links.length > 0 && (
                        <Chip label={`${entity.links.length} link(s)`} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      <IconButton size="small">
                        {expandedEntity === idx ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Stack>
                    <Collapse in={expandedEntity === idx}>
                      <Box sx={{ px: 2, pb: 1.5, bgcolor: '#fafafa' }}>
                        <Stack spacing={0.5}>
                          {entity.normalizedText && (
                            <Typography variant="body2">
                              <strong>Normalized:</strong> {entity.normalizedText}
                            </Typography>
                          )}
                          {entity.assertion && (
                            <Typography variant="body2">
                              <strong>Assertion:</strong>{' '}
                              {entity.assertion.certainty && `Certainty: ${entity.assertion.certainty}`}
                              {entity.assertion.conditionality && ` | Conditionality: ${entity.assertion.conditionality}`}
                              {entity.assertion.association && ` | Association: ${entity.assertion.association}`}
                            </Typography>
                          )}
                          {entity.links && entity.links.length > 0 && (
                            <Box>
                              <Typography variant="body2" fontWeight={600} mb={0.5}>Linked Codes:</Typography>
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {entity.links.map((link, li) => (
                                  <Chip
                                    key={li}
                                    label={`${link.dataSource}: ${link.id}`}
                                    size="small"
                                    color={link.dataSource.includes('ICD10') ? 'primary' : 'default'}
                                    variant="outlined"
                                    onClick={() => copyCode(link.id)}
                                    onDelete={() => copyCode(link.id)}
                                    deleteIcon={<CopyIcon sx={{ fontSize: 14 }} />}
                                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </Collapse>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* ══════════ Tab 1: ICD-10 Code Search ══════════ */}
      {mode === 1 && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Search the full ICD-10-CM code database (74,000+ codes). Powered by the{' '}
            <Link href="https://clinicaltables.nlm.nih.gov/" target="_blank" rel="noopener">
              NLM Clinical Table Search Service <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </Link>
          </Typography>

          <Stack direction="row" spacing={1} mb={2}>
            <TextField
              fullWidth
              placeholder='Search by diagnosis name or code (e.g. "diabetes", "E11", "hypertension")...'
              value={searchTerm}
              onChange={e => handleSearchChange(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchTerm(''); setSearchResults([]); setSearchTotal(0); setSearchError(null); }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearchSubmit}
              disabled={searching || !searchTerm.trim()}
              sx={{ textTransform: 'none', borderRadius: 5, fontWeight: 600, minWidth: 100 }}
            >
              Search
            </Button>
          </Stack>

          {searchError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSearchError(null)}>
              {searchError}
            </Alert>
          )}

          {searchResults.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Showing {searchResults.length} of {searchTotal.toLocaleString()} results
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell width={140}><strong>ICD-10-CM Code</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="right" width={80}><strong>Copy</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((r, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Chip
                            label={r.code}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{r.name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title={copiedCode === r.code ? 'Copied!' : 'Copy code'}>
                              <IconButton size="small" onClick={() => copyCode(r.code)}>
                                {copiedCode === r.code ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy code + description">
                              <IconButton size="small" onClick={() => copyCode(`${r.code}\t${r.name}`)}>
                                <CopyIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {searchTerm && !searching && searchResults.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No ICD-10-CM codes found for "{searchTerm}"
              </Typography>
            </Paper>
          )}

          {!searchTerm && (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#fafafa' }}>
              <MedicalIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" mb={1}>
                Start typing a diagnosis name or ICD-10 code to search
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                {['diabetes', 'hypertension', 'obesity', 'E11', 'I10', 'asthma', 'GERD', 'anxiety'].map(term => (
                  <Chip
                    key={term}
                    label={term}
                    size="small"
                    onClick={() => { setSearchTerm(term); searchICD10(term); }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {/* ══════════ Tab 2: Azure Settings ══════════ */}
      {mode === 2 && (
        <Box>
          <Typography variant="h6" fontWeight={600} mb={1}>
            Azure Text Analytics for Health Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            To use the clinical text analysis feature, you need an{' '}
            <Link href="https://learn.microsoft.com/en-us/azure/ai-services/language-service/text-analytics-for-health/quickstart" target="_blank" rel="noopener">
              Azure AI Language resource <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </Link>
            . Enter your endpoint and key below, or set them as environment variables{' '}
            (<code>REACT_APP_AZURE_LANGUAGE_ENDPOINT</code> and <code>REACT_APP_AZURE_LANGUAGE_KEY</code>).
            Settings are stored locally in your browser.
          </Typography>

          <Stack spacing={2} maxWidth={600}>
            <TextField
              label="Azure Language Endpoint"
              fullWidth
              placeholder="https://your-resource.cognitiveservices.azure.com"
              value={azureConfig.endpoint}
              onChange={e => setAzureConfig(prev => ({ ...prev, endpoint: e.target.value }))}
              helperText='Found in Azure Portal → Your Language Resource → Keys and Endpoint'
            />
            <TextField
              label="Azure Language Key"
              fullWidth
              type="password"
              placeholder="Your API key"
              value={azureConfig.key}
              onChange={e => setAzureConfig(prev => ({ ...prev, key: e.target.value }))}
              helperText="Either Key 1 or Key 2 from your Azure Language resource"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                onClick={handleSaveConfig}
                sx={{ textTransform: 'none', borderRadius: 5, fontWeight: 600 }}
              >
                Save Settings
              </Button>
              {configSaved && (
                <Chip icon={<CheckIcon />} label="Saved!" color="success" size="small" />
              )}
            </Stack>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" fontWeight={600} mb={1}>
            How to set up Azure Text Analytics for Health
          </Typography>
          <Box component="ol" sx={{ pl: 2.5, '& li': { mb: 0.75, fontSize: '0.9rem' } }}>
            <li>
              Go to the{' '}
              <Link href="https://portal.azure.com/#create/Microsoft.CognitiveServicesTextAnalytics" target="_blank" rel="noopener">
                Azure Portal
              </Link>{' '}
              and create a Language resource (or use an existing one)
            </li>
            <li>Select the <strong>Free (F0)</strong> tier for testing (5,000 text records/month free)</li>
            <li>After creation, go to <strong>Keys and Endpoint</strong> under Resource Management</li>
            <li>Copy the <strong>Endpoint</strong> and either <strong>Key 1</strong> or <strong>Key 2</strong></li>
            <li>Paste them in the fields above and click <strong>Save Settings</strong></li>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            The ICD-10 Code Search tab works without any Azure configuration. It uses the free{' '}
            <Link href="https://clinicaltables.nlm.nih.gov/" target="_blank" rel="noopener">
              NLM Clinical Tables API
            </Link>.
          </Alert>
        </Box>
      )}
    </Box>
  );
}
